<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    :header="$t('sendMail', { name: node.name })"
    visible
    modal
    class="popup popup--send-mail"
    @keyup.esc="closePopup(false)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(false)"
      />
    </template>
    <div class="p-fluid">
      <div class="field grid">
        <label for="name" class="col-12 mb-2"
          >{{ $t("field.to") }} <span class="required">*</span></label
        >
        <div class="col-12">
          <PrimeChips
            v-model="to"
            separator=" "
            :class="{ 'p-invalid': checkValidTo }"
            add-on-blur
          />
          <small class="p-error">{{ checkValidTo }}</small>
        </div>
      </div>
      <div class="send-mail-toggle-links">
        <button
          v-if="!showCc"
          type="button"
          class="send-mail-toggle-link"
          @click="showCc = true"
        >
          {{ $t("sendingMail.addCc") }}
        </button>
        <button
          v-if="!showCci"
          type="button"
          class="send-mail-toggle-link"
          @click="showCci = true"
        >
          {{ $t("sendingMail.addCci") }}
        </button>
      </div>
      <div v-if="showCc" class="field grid">
        <label for="name" class="col-12 mb-2">{{ $t("field.cc") }}</label>
        <div class="col-12">
          <PrimeChips
            v-model="cc"
            separator=" "
            :class="{ 'p-invalid': checkValidCC }"
            add-on-blur
          />
          <small class="p-error">{{ checkValidCC }}</small>
        </div>
      </div>
      <div v-if="showCci" class="field grid">
        <label for="name" class="col-12 mb-2">{{ $t("field.cci") }}</label>
        <div class="col-12">
          <PrimeChips
            v-model="cci"
            separator=" "
            :class="{ 'p-invalid': checkValidCCI }"
            add-on-blur
          />
          <small class="p-error">{{ checkValidCCI }}</small>
        </div>
      </div>
      <div class="field grid">
        <label for="name" class="col-12 mb-2"
          >{{ $t("field.subject") }} <span class="required">*</span></label
        >
        <div class="col-12">
          <PrimeInputText
            v-model="subject"
            type="text"
            :class="{ 'p-invalid': checkSubject }"
            @keyup.enter="sendMail"
          />
          <small class="p-error">{{ checkSubject }}</small>
        </div>
      </div>
      <div class="field grid">
        <label for="name" class="col-12 mb-2"
          >{{ $t("field.message") }} <span class="required">*</span></label
        >
        <div class="col-12">
          <PrimeTextarea
            v-model="message"
            :rows="8"
            :class="{ 'p-invalid': checkMessage }"
          />
          <small class="p-error">{{ checkMessage }}</small>
        </div>
      </div>
      <div class="field grid">
        <label for="name" class="col-12 mb-2">{{
          $t("field.shareLink")
        }}</label>
        <div class="col-12 copy-link">
          <PrimeInputText v-model="link" disabled />
          <PrimeButton severity="warning" @click="copyLinkClipboard"
            ><i class="ri-links-line" aria-hidden="true" />&nbsp;{{
              $t(`sharelink.copyLinkTooltip`)
            }}
          </PrimeButton>
        </div>
      </div>

      <div class="field grid">
        <div class="col-12">
          <PrimeCheckbox
            v-model="attachment"
            class="send-mail-attachment"
            name="attachment"
            binary
          />
          <label>{{ $t("addAttachment") }}</label>
        </div>
      </div>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('close')"
        severity="secondary"
        @click="closePopup"
      />
      <PrimeButton
        :label="$t('send')"
        :disabled="!checkAllForm"
        @click="sendMail"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import * as EmailValidator from "email-validator";
import { useSelectionStore } from "@/stores/selection.js";
import { useConfigStore } from "@/stores/config.js";
import { useUserStore } from "@/stores/user.js";
import { webscriptApi } from "@/AlfrescoApi.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import Button from "primevue/button";
import Chips from "primevue/chips";
import Textarea from "primevue/textarea";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";

export default {
  name: "SendMailPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeChips: Chips,
    PrimeTextarea: Textarea,
    PrimeCheckbox: Checkbox,
    PrimeInputText: InputText,
  },
  emits: ["close"],
  data() {
    return {
      node: {},
      to: [],
      cc: [],
      cci: [],
      showCc: false,
      showCci: false,
      subject: null,
      message: null,
      attachment: false,
      link: null,
    };
  },
  computed: {
    checkAllForm() {
      return (
        this.checkValidTo === null &&
        this.checkValidCC === null &&
        this.checkValidCCI === null &&
        this.checkSubject === null &&
        this.checkMessage === null
      );
    },
    checkValidTo() {
      if (this.to.length === 0) {
        return this.$t("sendingMail.errors.errorEmptyEmail");
      }

      let err = null;
      this.to.forEach((email) => {
        if (!EmailValidator.validate(email)) {
          err = this.$t("sendingMail.errors.errorValidEmail");
        }
      });
      return err;
    },
    checkValidCC() {
      let err = null;
      if (this.cc.length > 0) {
        this.cc.forEach((email) => {
          if (!EmailValidator.validate(email)) {
            err = this.$t("sendingMail.errors.errorValidEmail");
          }
        });
      }
      return err;
    },
    checkValidCCI() {
      let err = null;
      if (this.cci.length > 0) {
        this.cci.forEach((email) => {
          if (!EmailValidator.validate(email)) {
            err = this.$t("sendingMail.errors.errorValidEmail");
          }
        });
      }
      return err;
    },
    checkSubject() {
      if (this.subject === null || this.subject === "") {
        return this.$t("sendingMail.errors.errorEmptySubject");
      }
      if (this.subject.length < 6) {
        return this.$t("sendingMail.errors.errorSubjectTooShort");
      }
      return null;
    },
    checkMessage() {
      if (this.message === null || this.message === "") {
        return this.$t("sendingMail.errors.errorEmptyMessage");
      }
      return null;
    },
  },
  mounted() {
    this.node = useSelectionStore().files[0];
    this.to = [];
    this.cc = [];
    this.cci = [];

    // Default subject
    this.subject = this.$t("sendingMail.popup.defaultSubject");

    this.attachment = false;
    let shareId = this.node?.properties["qshare:sharedId"] ?? null;

    const config = useConfigStore();
    if (shareId) {
      this.link = `${config.PREVIEW_URL}${shareId}`;
    } else {
      // Automatically create a shared link if none exists
      alfrescoNodeService
        .createSharedLink(this.node)
        .then((data) => {
          this.link = `${config.PREVIEW_URL}${data.entry.id}`;
          this.buildDefaultMessage();
          // Force PDF rendition for preview
          alfrescoFileService.createRendition(this.node.id, "pdf");
        })
        .catch((err) => {
          console.error("Failed to create shared link", err);
          this.link = null;
        });
    }

    this.buildDefaultMessage();
  },
  methods: {
    buildDefaultMessage() {
      const userStore = useUserStore();
      const userName =
        userStore.person?.displayName || userStore.person?.id || "";
      const documentName =
        this.node?.properties["cm:title"] || this.node?.name || "";
      this.message = this.$t("sendingMail.popup.defaultMessage", {
        userName: userName,
        documentName: documentName,
        shareLink: this.link || "",
      });
    },
    closePopup(reload) {
      this.$emit("close", reload);
    },
    sendMail() {
      if (!this.checkAllForm) {
        console.warn("can't send email");
        return;
      }
      const params = {
        recipients: this.to.join(";"),
        recipientsInCopy: this.cc ? this.cc.join(";") : "",
        recipientsInHiddenCopy: this.cc ? this.cci.join(";") : "",
        object: this.subject,
        message: this.message,
        nodeId: this.node.id,
        isAttachment: this.attachment,
      };
      return webscriptApi
        .executeWebScript("POST", "fr/jeci/sendMail", params)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("sendMailSuccess"),
            life: 5000,
          });
          this.closePopup();
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("sendingMail.errors.errorSendMail"),
            detail: err,
            life: 5000,
          });
        });
    },
    copyLinkClipboard() {
      navigator.clipboard.writeText(this.link).then(() => {
        this.$toast.add({
          severity: "success",
          summary: this.$t("share.copyLinkToastSummary"),
          detail: this.$t("share.copyLinkToastDetail"),
          life: 3000,
        });
      });
    },
  },
};
</script>
<style lang="css" scoped>
:deep(.popup--send-mail) {
  width: 50rem;
  max-width: 90vw;
}

.send-mail-attachment {
  margin-right: 0.5rem;
}

.copy-link {
  display: flex;
  flex-direction: row;
}

.send-mail-toggle-links {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.send-mail-toggle-link {
  background: none;
  border: none;
  color: var(--p-primary-color);
  cursor: pointer;
  padding: 0;
  font-size: 0.875rem;
  text-decoration: underline;
}

.send-mail-toggle-link:hover {
  color: var(--p-primary-700);
}

.send-mail-toggle-link:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
